import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';

const PlanetSyncContext = createContext(null);

export function usePlanetSync() {
  return useContext(PlanetSyncContext) || { state: DEFAULT_STATE };
}

const DEFAULT_STATE = {
  enabled: true,
  mode: 'automatic',
  environment: 'environment_01',
  time_profile: 'automatic',
  weather: 'automatic',
  mood: 'calm',
  animation_intensity: 0.6,
  overlay_strength: 0.6,
  motion_enabled: true,
};

// Resolve automatic time_profile based on current hour (local)
export function resolveTimeProfile(time_profile) {
  if (time_profile && time_profile !== 'automatic') return time_profile;
  const h = new Date().getHours();
  if (h < 5) return 'night';
  if (h < 7) return 'sunrise';
  if (h < 11) return 'morning';
  if (h < 15) return 'afternoon';
  if (h < 18) return 'golden_hour';
  if (h < 20) return 'sunset';
  if (h < 22) return 'evening';
  return 'night';
}

export function PlanetSyncProvider({ children }) {
  const [state, setState] = useState(DEFAULT_STATE);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/planetsync').then(({ data }) => {
      if (active && data) setState((s) => ({ ...s, ...data }));
    }).catch(() => {}).finally(() => setLoaded(true));
    return () => { active = false; };
  }, []);

  // Re-poll every 5 minutes to catch admin changes (lightweight)
  useEffect(() => {
    const id = setInterval(() => {
      api.get('/planetsync').then(({ data }) => data && setState((s) => ({ ...s, ...data }))).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const update = useCallback(async (patch) => {
    const { data } = await api.put('/admin/planetsync', patch);
    if (data) setState((s) => ({ ...s, ...data }));
    return data;
  }, []);

  const value = { state, loaded, update, resolvedTime: resolveTimeProfile(state.time_profile) };
  return <PlanetSyncContext.Provider value={value}>{children}</PlanetSyncContext.Provider>;
}
